import React from 'react';
import { Server, Shield, AlertTriangle, Info, Code } from 'lucide-react';
const SERVICE_DESCRIPTIONS = {
  http: 'Web server:serves unencrypted HTTP traffic',
  https: 'Secure web server:serves TLS-encrypted HTTPS traffic',
  'https-alt': 'Alternate HTTPS port:typically used for admin panels or APIs',
  dns: 'Domain Name System:resolves hostnames to IP addresses',
  ssh: 'Secure Shell:encrypted remote access and file transfer',
  ftp: 'File Transfer Protocol:unencrypted file transfers (insecure)',
  smtp: 'Simple Mail Transfer Protocol:email delivery',
  pop3: 'Post Office Protocol:email retrieval',
  imap: 'Internet Message Access Protocol:email retrieval',
  mysql: 'MySQL database server',
  postgresql: 'PostgreSQL database server',
  redis: 'Redis in-memory data store',
  mongodb: 'MongoDB NoSQL database',
  telnet: 'Telnet:unencrypted remote access (insecure)',
  rdp: 'Remote Desktop Protocol:Windows remote access',
  smb: 'Server Message Block:Windows file sharing',
  vnc: 'Virtual Network Computing:remote desktop',
  ldap: 'Lightweight Directory Access Protocol:directory services',
  kerberos: 'Kerberos:network authentication protocol',
  ntp: 'Network Time Protocol:time synchronization',
  snmp: 'Simple Network Management Protocol:device monitoring',
};

const PortScanResults = ({ results }) => {
  const isTrial = false;

  // For trial users, slice to first visible + hidden remainder
  const visibleResults = isTrial && results.length > 1 ? results.slice(0, 1) : results;
  const hiddenResults = isTrial && results.length > 1 ? results.slice(1) : [];

  // Group ports by state — use visibleResults for the main sections
  const openPorts = visibleResults.filter(r => r.data?.state === 'open');
  const closedPorts = visibleResults.filter(r => r.data?.state === 'closed');
  const filteredPorts = visibleResults.filter(r => r.data?.state === 'filtered');

  const getServiceColor = (service) => {
    const dangerousServices = ['telnet', 'ftp', 'smb', 'rdp', 'vnc'];
    if (dangerousServices.includes(service?.toLowerCase())) {
      return 'text-red-600';
    }
    return 'text-shinobi-orange';
  };

  const renderPort = (result) => {
    const data = result.data || {};
    const { port, protocol, service, product, version, extrainfo, banner, scripts } = data;
    const serviceColor = getServiceColor(service);
    const description = SERVICE_DESCRIPTIONS[service?.toLowerCase()] || '';

    return (
      <div key={result.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:border-shinobi-purple/30 transition-colors">
        {/* Port Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <Server className={`w-5 h-5 ${serviceColor}`} />
            <div>
              <span className="text-xl font-bold text-slate-800 font-mono">
                {port}/{protocol}
              </span>
              <span className={`ml-3 px-2 py-1 rounded text-xs font-semibold ${serviceColor} bg-cyan-900/20`}>
                {service || 'unknown'}
              </span>
            </div>
          </div>
          {result.is_new && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-shinobi-purple/10 text-shinobi-purple">
              NEW
            </span>
          )}
        </div>

        {/* Service Description */}
        {description && (
          <p className="text-xs text-gray-500 mb-3 ml-8">{description}</p>
        )}

        {/* Service Details */}
        {(product || version) && (
          <div className="mb-3 text-sm text-slate-500 ml-8">
            <span className="font-semibold">{product}</span>
            {version && <span className="ml-2 text-shinobi-purple">{version}</span>}
            {extrainfo && <span className="ml-2 text-gray-500">({extrainfo})</span>}
          </div>
        )}

        {/* Banner */}
        {banner && (
          <div className="ml-8 mb-2 px-3 py-2 bg-slate-50 rounded text-xs font-mono text-gray-500 truncate">
            {banner}
          </div>
        )}

        {/* Script Output */}
        {scripts && scripts.length > 0 && (
          <details className="mt-3">
            <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700 flex items-center space-x-2">
              <Code className="w-4 h-4" />
              <span>Script Output ({scripts.length})</span>
            </summary>
            <div className="mt-2 space-y-2">
              {scripts.map((script, idx) => (
                <div key={idx} className="bg-slate-50 rounded p-3">
                  <div className="text-xs font-semibold text-shinobi-orange mb-1">{script.id}</div>
                  <pre className="text-xs text-slate-500 whitespace-pre-wrap">{script.output}</pre>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Open Ports */}
      {openPorts.length > 0 && (
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Shield className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-bold text-slate-800">Open Ports</h3>
            <span className="text-sm text-gray-500">({openPorts.length})</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {openPorts.map(renderPort)}
          </div>
        </div>
      )}

      {/* Filtered Ports */}
      {filteredPorts.length > 0 && (
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <h3 className="text-lg font-bold text-slate-800">Filtered Ports</h3>
            <span className="text-sm text-gray-500">({filteredPorts.length})</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredPorts.map(renderPort)}
          </div>
        </div>
      )}

      {/* Closed Ports */}
      {closedPorts.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm text-gray-500 cursor-pointer hover:text-slate-500 flex items-center space-x-2">
            <Info className="w-4 h-4" />
            <span>Closed Ports ({closedPorts.length})</span>
          </summary>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {closedPorts.map(renderPort)}
          </div>
        </details>
      )}

      {/* Trial blur for hidden ports */}
      {hiddenResults.length > 0 && (
        
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {hiddenResults.map(renderPort)}
          </div>
        
      )}
    </div>
  );
};

export default PortScanResults;
